/**
 * Board-header "…" menu for the current tracker. Groups the low-frequency
 * board actions behind one kebab (mirrors the BriefDetailHeader pattern):
 *   - "Edit details" — the edit sheet, for the owner or an admin.
 *   - "Save as template" — snapshot the board's structure as a reusable
 *     template, which also takes the board's icon and colour.
 * Ownership/visibility is enforced server-side, and a refused write surfaces an
 * error toast.
 */
import { useRef, useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import { LayoutTemplate, MoreVertical, Pencil } from 'lucide-react';

import { type Tracker } from '@/entities/tracker';
import { useModalState } from '@/shared/hooks';
import { useTranslation } from 'react-i18next';
import { EditTrackerSheet } from './components/EditTrackerSheet';
import { SaveTrackerTemplateDialog } from './components/SaveTrackerTemplateDialog';
import { useTrackerAccess } from './hooks/useTrackerAccess';

export function TrackerDetailsMenu({
  tracker,
  size = 'default',
}: {
  tracker: Tracker;
  /** `lg` is the 40px rung the redesigned board header sits its actions on. */
  size?: 'default' | 'lg';
}) {
  const { t } = useTranslation('trackers');
  const { canManage } = useTrackerAccess(tracker);

  const [editOpen, setEditOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const saveDialog = useModalState();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            ref={menuTriggerRef}
            variant="ghost"
            // The kebab is square in the redesign, where the header draws it as
            // an icon button rather than a text button holding one glyph.
            size={size === 'lg' ? 'icon-lg' : 'default'}
            aria-label={t('trackers.trackerDetailsMenu.trackerActions')}
          >
            <MoreVertical className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canManage ? (
            <DropdownMenuItem onSelect={() => setEditOpen(true)}>
              <Pencil className="size-4" aria-hidden />
              {t('trackers.trackerDetailsMenu.editDetails')}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onSelect={saveDialog.open}>
            <LayoutTemplate className="size-4" aria-hidden />
            {t('trackers.trackerDetailsMenu.saveAsTemplate')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <EditTrackerSheet
        tracker={tracker}
        open={editOpen}
        onOpenChange={setEditOpen}
        returnFocusTo={menuTriggerRef}
      />

      {saveDialog.isMounted ? (
        <SaveTrackerTemplateDialog
          key={saveDialog.instanceKey}
          tracker={tracker}
          open={saveDialog.isOpen}
          onClose={saveDialog.close}
        />
      ) : null}
    </>
  );
}
